import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Space,
  Badge,
  Alert,
  Tabs,
  Spin,
} from "antd";
import {
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  AlertOutlined,
  ScheduleOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { Navigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  fetchProductionDashboard,
  fetchStageJobCounts,
  fetchTodaySchedule,
} from "../../api/dashboardApi";
import StageStatusChart from "./charts/StageStatusChart";
import MachineUtilization from "./widgets/MachineUtilization";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const ProductionDashboard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [stageData, setStageData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [delayedJobs, setDelayedJobs] = useState([]);

  // ── Data fetch ─────────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    try {
      const data = await fetchProductionDashboard();

      // Production summary
      setSummary(data.productionReport?.summary || null);

      // Job completion → delayed jobs + on-time stats
      const comp = data.jobCompletionReport;
      setCompletion(comp || null);
      if (comp?.jobs) {
        setDelayedJobs(comp.jobs.filter((j) => !j.on_time));
      }

      // Alerts – compose from maintenance-due + delayed
      const alertList = [];
      const maintenanceMachines = Array.isArray(data.maintenanceDue)
        ? data.maintenanceDue
        : data.maintenanceDue?.machines || [];

      maintenanceMachines.forEach((m) => {
        alertList.push({
          severity: "MEDIUM",
          type: "MAINTENANCE_DUE",
          message: `${m.name || m.code} – maintenance due ${
            m.next_maintenance
              ? moment(m.next_maintenance).fromNow()
              : "soon"
          }`,
          created_at: new Date().toISOString(),
        });
      });

      if (comp?.delayed > 3) {
        alertList.unshift({
          severity: "HIGH",
          type: "DELAYED_JOBS",
          message: `${comp.delayed} job(s) are overdue – review production schedule`,
          created_at: new Date().toISOString(),
        });
      }

      // Machines in breakdown
      (data.machines || [])
        .filter((m) => m.status === "breakdown")
        .forEach((m) => {
          alertList.unshift({
            severity: "HIGH",
            type: "MACHINE_BREAKDOWN",
            message: `${m.name || m.code} is in breakdown – production blocked`,
            created_at: new Date().toISOString(),
          });
        });

      setAlerts(alertList);

      // Stage-level job counts (from default flow stages)
      if (data.flow?.stages) {
        const stages = data.flow.stages.sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0)
        );
        const counts = await fetchStageJobCounts(stages);
        setStageData(counts);
      }
    } catch (error) {
      toast.error("Failed to load production dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    // Refresh every 5 minutes
    const interval = setInterval(fetchDashboard, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Jobs-in-progress count from stage data ─────────────────
  const jobsInProgress = stageData.reduce((sum, s) => {
    const inProg = (s.jobs || []).filter(
      (j) => j.status === "in_progress" || j.status === "running"
    ).length;
    return sum + inProg;
  }, 0);

  const totalJobsAcrossStages = stageData.reduce(
    (sum, s) => sum + (s.total || 0),
    0
  );

  // ── Delayed jobs columns ───────────────────────────────────
  const delayedJobColumns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      key: "job_number",
      render: (val) => (
        <Link to={`/job/${val}`} style={{ fontWeight: 500 }}>
          {val}
        </Link>
      ),
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: true,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (val) => {
        const color =
          val === "urgent"
            ? "red"
            : val === "high"
            ? "orange"
            : "default";
        return <Tag color={color}>{(val || "normal").toUpperCase()}</Tag>;
      },
    },
    {
      title: "Expected Delivery",
      dataIndex: "expected_delivery",
      key: "expected_delivery",
      render: (val) => (val ? moment(val).format("DD-MMM-YYYY") : "—"),
    },
    {
      title: "Completed At",
      dataIndex: "completed_at",
      key: "completed_at",
      render: (val) =>
        val ? moment(val).format("DD-MMM-YYYY") : "Not completed",
    },
    {
      title: "Ordered Qty",
      dataIndex: "ordered_qty",
      key: "ordered_qty",
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: "Completed Qty",
      dataIndex: "completed_qty",
      key: "completed_qty",
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: "Rejected",
      dataIndex: "rejected_qty",
      key: "rejected_qty",
      render: (v) =>
        v > 0 ? <Tag color="red">{v.toLocaleString()}</Tag> : "0",
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <PageTitle title="Back" subtitle="Production Dashboard" />
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle title="Back" subtitle="Production Dashboard" />

      {/* Critical Alerts */}
      {alerts.filter((a) => a.severity === "HIGH").length > 0 && (
        <Alert
          type="error"
          message="Production Alerts"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {alerts
                .filter((a) => a.severity === "HIGH")
                .map((alert, i) => (
                  <li key={i}>{alert.message}</li>
                ))}
            </ul>
          }
          showIcon
          icon={<AlertOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Jobs (Active)"
              value={totalJobsAcrossStages}
              prefix={<SwapOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Completed (30d)"
              value={completion?.total_completed || summary?.total_jobs_completed || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Delayed Jobs"
              value={delayedJobs.length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{
                color: delayedJobs.length > 0 ? "#f5222d" : "#52c41a",
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="On-Time Rate"
              value={completion?.on_time_percentage || 0}
              suffix="%"
              prefix={<ScheduleOutlined />}
              valueStyle={{
                color:
                  (completion?.on_time_percentage || 0) >= 90
                    ? "#52c41a"
                    : "#faad14",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Stage Status Chart */}
        <Col xs={24} lg={16}>
          <Card
            title="Production Stage Status"
            bodyStyle={{ height: 400 }}
          >
            <StageStatusChart data={stageData} />
          </Card>
        </Col>

        {/* Machine Utilization */}
        <Col xs={24} lg={8}>
          <Card title={<><ToolOutlined /> Machine Utilization</>}>
            <MachineUtilization />
          </Card>
        </Col>
      </Row>

      {/* Tabs for different views */}
      <Card style={{ marginTop: 16 }}>
        <Tabs
          defaultActiveKey="delayed"
          items={[
            {
              key: "delayed",
              label: (
                <span>
                  <WarningOutlined /> Delayed Jobs ({delayedJobs.length})
                </span>
              ),
              children: (
                <Table
                  columns={delayedJobColumns}
                  dataSource={delayedJobs}
                  rowKey={(r) => r.job_number}
                  pagination={false}
                  size="small"
                  locale={{ emptyText: "No delayed jobs 🎉" }}
                />
              ),
            },
            {
              key: "today",
              label: (
                <span>
                  <ScheduleOutlined /> Today's Schedule
                </span>
              ),
              children: <TodaySchedule />,
            },
            {
              key: "all-alerts",
              label: `All Alerts (${alerts.length})`,
              children: <AlertsList alerts={alerts} />,
            },
          ]}
        />
      </Card>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// Sub-component: Today's Schedule
// ═══════════════════════════════════════════════════════════════
const TodaySchedule = () => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodaySchedule()
      .then((data) => {
        setSchedule(data || []);
      })
      .catch(() => {
        /* silent */
      })
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    {
      title: "Job",
      dataIndex: "title",
      key: "title",
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: true,
    },
    {
      title: "Machine",
      dataIndex: "machine_name",
      key: "machine_name",
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      render: (val, r) => <Tag color={r.color || "blue"}>{val}</Tag>,
    },
    {
      title: "Start",
      dataIndex: "start",
      key: "start",
      render: (val) => (val ? moment(val).format("HH:mm") : "—"),
    },
    {
      title: "End",
      dataIndex: "end",
      key: "end",
      render: (val) => (val ? moment(val).format("HH:mm") : "—"),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (val) => {
        const color =
          val === "urgent"
            ? "red"
            : val === "high"
            ? "orange"
            : "default";
        return <Tag color={color}>{(val || "normal").toUpperCase()}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        const color =
          val === "completed"
            ? "green"
            : val === "in_progress" || val === "running"
            ? "blue"
            : "default";
        return <Tag color={color}>{(val || "").replace(/_/g, " ").toUpperCase()}</Tag>;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={schedule}
      rowKey={(r) => r.id || Math.random()}
      loading={loading}
      pagination={false}
      size="small"
      locale={{ emptyText: "No schedules for today" }}
    />
  );
};

// ═══════════════════════════════════════════════════════════════
// Sub-component: Alerts List
// ═══════════════════════════════════════════════════════════════
const AlertsList = ({ alerts }) => {
  const columns = [
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 120,
      render: (val) => (
        <Badge
          status={
            val === "HIGH"
              ? "error"
              : val === "MEDIUM"
              ? "warning"
              : "default"
          }
          text={val}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (val) => <Tag>{(val || "").replace(/_/g, " ")}</Tag>,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
    {
      title: "Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (val) => (val ? moment(val).format("DD-MMM-YYYY HH:mm") : "—"),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={alerts}
      rowKey={(_, i) => i}
      pagination={{ pageSize: 10 }}
      size="small"
      locale={{ emptyText: "No alerts" }}
    />
  );
};

export default ProductionDashboard;
